import React, { useState, useCallback, useMemo } from 'react';
import { Search, HelpCircle, X } from 'lucide-react';

interface SearchSuggestion {
  type: 'token' | 'document';
  text: string;
  description: string;
  insertText?: string;
}

interface AdvancedSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onSearch: (query: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  searchResults?: Array<{ id: string; title: string; original_filename: string }>;
  onSelectResult?: (result: { id: string; title: string; original_filename: string }) => void;
}

export const AdvancedSearchInput: React.FC<AdvancedSearchInputProps> = ({
  value,
  onChange,
  onSearch,
  placeholder = "Search documents",
  className = "",
  disabled = false,
  searchResults = [],
  onSelectResult,
}) => {
  const [showHelp, setShowHelp] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);

  const suggestions: SearchSuggestion[] = useMemo(() => {
    const tokenSuggestions: SearchSuggestion[] = [
      {
        type: 'token',
        text: 'vendor:',
        description: 'Search by vendor name',
        insertText: 'vendor:"'
      },
      {
        type: 'token', 
        text: 'content:',
        description: 'Search document content',
        insertText: 'content:"'
      },
      {
        type: 'token',
        text: '~"',
        description: 'Vector/semantic search',
        insertText: '~"'
      },
    ];

    // Add document results as suggestions
    const documentSuggestions: SearchSuggestion[] = searchResults.slice(0, 5).map(doc => ({
      type: 'document',
      text: doc.title || doc.original_filename,
      description: doc.original_filename !== (doc.title || doc.original_filename) ? doc.original_filename : 'Document',
      insertText: doc.title || doc.original_filename
    }));

    return [...tokenSuggestions, ...documentSuggestions];
  }, [searchResults]);

  const filteredSuggestions = useMemo(() => {
    if (!value || !showSuggestions) return [];
    
    const query = value.toLowerCase();
    return suggestions.filter(s => 
      s.text.toLowerCase().includes(query) ||
      s.description.toLowerCase().includes(query)
    ).slice(0, 6);
  }, [value, showSuggestions, suggestions]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setCursorPosition(e.target.selectionStart || 0);
    setShowSuggestions(newValue.length > 0);
  }, [onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSearch(value);
      setShowSuggestions(false);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  }, [value, onSearch]);

  const handleSuggestionClick = useCallback((suggestion: SearchSuggestion) => {
    if (suggestion.type === 'document') {
      // For document suggestions, navigate to the document
      const selectedDoc = searchResults.find(doc => 
        (doc.title || doc.original_filename) === suggestion.text
      );
      if (selectedDoc && onSelectResult) {
        onSelectResult(selectedDoc);
        setShowSuggestions(false);
        return;
      }
      // Or just search for the document title
      onChange(suggestion.text);
      onSearch(suggestion.text);
    } else if (suggestion.insertText) {
      // Insert token suggestions at current cursor position
      const newValue = value.slice(0, cursorPosition) + suggestion.insertText + value.slice(cursorPosition);
      onChange(newValue);
    } else {
      onChange(suggestion.text);
    }
    setShowSuggestions(false);
  }, [value, cursorPosition, onChange, onSearch, searchResults, onSelectResult]);

  const handleSearchClick = useCallback(() => {
    onSearch(value);
    setShowSuggestions(false);
  }, [value, onSearch]);

  const handleClearClick = useCallback(() => {
    onChange('');
    setShowSuggestions(false);
  }, [onChange]);

  const helpContent = (
    <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 p-4">
      <div className="space-y-4">
        <div>
          <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Search Syntax</h4>
          <div className="space-y-2 text-sm">
            <div>
              <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">vendor:"Company Name"</code>
              <span className="ml-2 text-gray-600 dark:text-gray-400">Search by vendor name</span>
            </div>
            <div>
              <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">content:"search terms"</code>
              <span className="ml-2 text-gray-600 dark:text-gray-400">Search document content</span>
            </div>
            <div>
              <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">~"semantic query"</code>
              <span className="ml-2 text-gray-600 dark:text-gray-400">Vector/semantic search</span>
            </div>
            <div>
              <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">"exact phrase"</code>
              <span className="ml-2 text-gray-600 dark:text-gray-400">Search titles and filenames</span>
            </div>
          </div>
        </div>
        
      </div>
    </div>
  );

  const suggestionsContent = (
    <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-40 max-h-64 overflow-y-auto">
      {filteredSuggestions.map((suggestion, index) => (
        <button
          key={index}
          onClick={() => handleSuggestionClick(suggestion)}
          className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-600 last:border-b-0 focus:outline-none focus:bg-gray-50 dark:focus:bg-gray-700"
        >
          <div className="flex items-center space-x-3">
            <div className={`px-2 py-1 rounded text-xs font-medium ${
              suggestion.type === 'token' 
                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
            }`}>
              {suggestion.type === 'token' ? 'Syntax' : 'Document'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                {suggestion.text}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                {suggestion.description}
              </div>
            </div>
          </div>
        </button>
      ))}
    </div>
  );

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(value.length > 0)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)} // Delay to allow suggestion clicks
          placeholder={placeholder}
          disabled={disabled}
          className="block w-full pl-10 pr-20 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
        />
        
        <div className="absolute inset-y-0 right-0 flex items-center space-x-1 pr-3">
          {value && (
            <button
              onClick={handleClearClick}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
              title="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
            title="Search help"
          >
            <HelpCircle className="h-4 w-4" />
          </button>
          
          {value && (
            <button
              onClick={handleSearchClick}
              disabled={disabled}
              className="ml-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              Search
            </button>
          )}
        </div>
      </div>
      
      {showHelp && helpContent}
      {showSuggestions && filteredSuggestions.length > 0 && suggestionsContent}
    </div>
  );
};